import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './GameInfo.css';
import CarmenImage from "../../assets/images/carmen.png";
import Button from '../../components/Button/Button3';
import GameSummary from '../GameSummary/GameSummary';
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import { formatDayFirstDateTime } from "../../utils/dateFormatting";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { resolveDisplayedGameOutcome } from "../../utils/gameSummary";
import { buildPlayerPath, buildProfilePath, isOwnPlayerPath } from "../../utils/appRoutes";

/**
 * Helper to convert global outcome into user perspective:
 * - 'Team 1 wins' => userTeamIndex === 0 ? 'You Won!' : 'You Lost!'
 * - 'Team 2 wins' => userTeamIndex === 1 ? 'You Won!' : 'You Lost!'
 * - 'Draw'        => 'You Drew!'
 */
function getUserOutcome(gameOutcome, userTeamIndex) {
  if (!gameOutcome) return '';
  if (gameOutcome === 'Draw') return 'You Drew!';

  if (gameOutcome === 'Team 1 wins') {
    return userTeamIndex === 0 ? 'You Won!' : 'You Lost!';
  }
  if (gameOutcome === 'Team 2 wins') {
    return userTeamIndex === 1 ? 'You Won!' : 'You Lost!';
  }

  return '';
}

function formatDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDayFirstDateTime(parsed);
}

function buildTimeWindowLabel(startTime, durationValue) {
  const rawStart = String(startTime || "").trim();
  const durationMinutes = Number(durationValue);
  const hasDuration = Number.isFinite(durationMinutes) && durationMinutes > 0;

  if (!rawStart && !hasDuration) return "";
  if (!rawStart && hasDuration) return `${Math.round(durationMinutes)} min`;
  if (!hasDuration) return rawStart;

  const timeParts = rawStart.split(":");
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);
  const validStart = Number.isInteger(hours) && Number.isInteger(minutes);

  if (!validStart) {
    return `${rawStart} (${Math.round(durationMinutes)} min)`;
  }

  const base = new Date(2000, 0, 1, hours, minutes, 0, 0);
  base.setMinutes(base.getMinutes() + Math.round(durationMinutes));
  const endHours = String(base.getHours()).padStart(2, "0");
  const endMinutes = String(base.getMinutes()).padStart(2, "0");
  const endTime = `${endHours}:${endMinutes}`;

  // return `${rawStart} - ${endTime} (${Math.round(durationMinutes)} min)`;
  return `${rawStart} - ${endTime} `;

}

function getPlayerDisplayName(player) {
  const firstName = String(player?.first_name || "").trim();
  const lastName = String(player?.last_name || "").trim();

  if (firstName && lastName) {
    return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
  }

  if (firstName) {
    return firstName;
  }

  return player?.email || "Unknown player";
}

function getPlayerMeta(player) {
  const position = String(player?.position || "").trim();
  if (position && position !== "Unknown") {
    return position;
  }

  return null;
}

function getPlayerProfilePath(player, currentUserId) {
  const userId = String(player?.userId || "").trim();
  if (!userId) return null;
  return isOwnPlayerPath(userId, currentUserId) ? buildProfilePath() : buildPlayerPath(userId);
}

const GameInfo = ({
  time,
  date,
  duration,
  gameName,
  tournament,
  stadiumAmenities = [],
  stadiumType = "",
  stadiumSurface = "",
  placesLeft,
  onSignUp,
  isSignedUp,
  onWaitlistToggle,
  isWaitlisted,
  isGameFull,
  gameStatus,
  gameOutcome,
  gameResult,
  userTeamIndex,
  userPlayerData,
  currentUserId = null,
  gameImagePath,
  teams = [[], []],
  waitlist = [],
  matchEvents = [],
}) => {

  // We'll fetch images from the backend if present, else default to CarmenImage
  const gameImage = resolveMediaUrl(gameImagePath, CarmenImage);

  // NEW: local state to toggle the "See who's playing!" list
  const [showPlayers, setShowPlayers] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const actionButtonVariant = isSmallScreen ? "small" : "medium";
  const cancelButtonStyle = isSmallScreen ? "default" : "cancel";

  const teamOnePlayers = Array.isArray(teams[0]) ? teams[0].filter(Boolean) : [];
  const teamTwoPlayers = Array.isArray(teams[1]) ? teams[1].filter(Boolean) : [];
  const waitlistPlayers = Array.isArray(waitlist) ? waitlist.filter(Boolean) : [];
  const isFinishedGame = gameStatus === 'finished' || gameStatus === 'settled';
  const placesLeftCount = Number(placesLeft);
  const isPlacesLow = placesLeftCount <= 2;
  const isPlacesFull = isGameFull || placesLeftCount <= 0;
  const placesLeftNote = isPlacesFull
    ? (isWaitlisted
        ? "You're on the waitlist. We'll notify you if a spot opens."
        : (!isSignedUp ? "Join the waitlist to get notified when a spot opens." : null))
    : null;
  const rosterSections = [
    { key: "team-1", title: "Team 1", players: teamOnePlayers },
    { key: "team-2", title: "Team 2", players: teamTwoPlayers },
    { key: "waitlist", title: "Waitlist", players: waitlistPlayers, isWaitlist: true },
  ].filter((section) => section.players.length > 0);
  const isCashPaid = userPlayerData?.paymentStatus === 'paid_cash';
  const userCashPaidAt = formatDateTime(userPlayerData?.cashPaidAt);
  const timeWindowLabel = buildTimeWindowLabel(time, duration);
  const normalizedAmenities = Array.isArray(stadiumAmenities)
    ? stadiumAmenities
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const visibleAmenities = normalizedAmenities.slice(0, 6);
  const remainingAmenitiesCount = normalizedAmenities.length - visibleAmenities.length;
  const hasStadiumInfo = !isFinishedGame;

  const stadiumInfoBlock = hasStadiumInfo ? (
    <div className="game-stadium-info">
      <div className="game-stadium-info-title">Stadium Info</div>
      {(stadiumType || stadiumSurface) && (
        <div className="game-stadium-tags">
          {stadiumType && <span className="game-stadium-tag">{stadiumType}</span>}
          {stadiumSurface && <span className="game-stadium-tag">{stadiumSurface}</span>}
        </div>
      )}
      {visibleAmenities.length > 0 ? (
        <div className="game-amenities">
          {visibleAmenities.map((amenity, index) => (
            <span key={`${amenity}-${index}`} className="game-amenity-pill">
              {amenity}
            </span>
          ))}
          {remainingAmenitiesCount > 0 && (
            <span className="game-amenity-pill game-amenity-pill-more">
              +{remainingAmenitiesCount} more
            </span>
          )}
        </div>
      ) : (
        <div className="game-stadium-empty">No amenities listed yet</div>
      )}
    </div>
  ) : null;

  // Toggle handler
  const handleTogglePlayers = () => {
    setShowPlayers((prev) => !prev);
  };

  // If game is finished, display user outcome
  if (isFinishedGame) {
    const displayedGameOutcome = resolveDisplayedGameOutcome({
      result: gameResult,
      gameOutcome,
      matchEvents,
    });
    const userResult = userTeamIndex !== null
      ? getUserOutcome(displayedGameOutcome, userTeamIndex)
      : null;
    const computedImage = resolveMediaUrl(gameImagePath, CarmenImage);
    const gameImageStyle = { "--game-bg-image": `url(${computedImage})` };

    return (
      <div className="game-info-container game-info-container--summary" style={gameImageStyle}>
        <div className="game-info game-info--summary">
          <div className="game-main-copy game-main-copy--summary">
            <div className="game-info-sub2 game-info-sub2--summary">
              <div className="game-result">
                <GameSummary
                  result={gameResult}
                  matchEvents={matchEvents}
                  teams={teams}
                  userResult={userResult}
                  timeLabel={timeWindowLabel || time}
                  dateLabel={date}
                  gameName={gameName}
                  tournament={tournament}
                  gameStatus={gameStatus}
                  currentUserId={currentUserId}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="game-image-container">
          <img src={computedImage} alt="Game" className="game-image" />
          <div className="noiseeffect"></div>
        </div>
      </div>
    );
  }

  // Otherwise, game is not finished => normal sign-up UI
  return (
    <div className="game-info-container" style={{ "--game-bg-image": `url(${gameImage})` }}>
      <div className="game-info">
        <div className="game-main-copy">
          <div className="game-info-sub2">
            <div className="game-game-name">{gameName}</div>
            <div className="game-info-sub1">
              <div className="game-time">{timeWindowLabel || time}</div>
              <div className="game-date">{date}</div>
            </div>
            {/* <div className="game-tournament">{tournament}</div> */}
            {stadiumInfoBlock}

            <div className="game-places-left">
              <div className="game-places-left-sign">
                <span className={isPlacesLow ? "game-places-left-count game-places-left-count--full" : "game-places-left-count"}>
                  {placesLeft} places left
                </span>
                {placesLeftNote && (
                  <div className="game-places-left-note">{placesLeftNote}</div>
                )}
                <span className="see-whos-playing" onClick={handleTogglePlayers}>
                  {showPlayers ? "Hide lineup" : "See game lineup"}
                </span>
              </div>

              {isSignedUp && userPlayerData?.paymentMethod === 'cash' && (
                <div
                  className={`game-payment-banner ${
                    isCashPaid ? 'game-payment-banner--paid' : 'game-payment-banner--pending'
                  }`}
                >
                  {isCashPaid
                    ? `Cash payment received${userCashPaidAt ? ` on ${userCashPaidAt}` : ''}.`
                    // : `Cash payment pending${userCashDue ? ` until ${userCashDue}` : ''}.`}
                    : `Cash payment pending.`}
                    </div>
              )}

              {/* Move the player-list directly below the link, inside .game-places-left */}
              {showPlayers && (
                <div className="player-list">
                  <div className="player-list-header">
                    <h4>Game lineup</h4>
                  </div>
                  <p className="player-list-note">Final teams might still change.</p>

                  {rosterSections.length > 0 ? (
                    <div className="player-list-sections">
                      {rosterSections.map((section) => (
                        <div
                          key={section.key}
                          className={`player-list-section ${
                            section.isWaitlist ? "player-list-section--waitlist" : ""
                          }`}
                        >
                          <div className="player-list-section-header">
                            <span className="player-list-section-title">{section.title}</span>
                            <span className="player-list-section-line" />
                          </div>
                          <ul className="player-list-items">
                            {section.players.map((player, idx) => {
                              const meta = getPlayerMeta(player);
                              const playerProfilePath = getPlayerProfilePath(player, currentUserId);
                              return (
                                <li
                                  key={`${section.key}-${player?.email || idx}`}
                                  className="player-list-item"
                                >
                                  {playerProfilePath ? (
                                    <Link
                                      to={playerProfilePath}
                                      className="player-list-name player-list-name-link"
                                    >
                                      {getPlayerDisplayName(player)}
                                    </Link>
                                  ) : (
                                    <span className="player-list-name">
                                      {getPlayerDisplayName(player)}
                                    </span>
                                  )}
                                  {meta && (
                                    <span className="player-list-meta">{meta}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="player-list-empty">No players have joined yet.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sign-up-button-container">
          {/* sign up / waitlist logic */}
          {isSignedUp ? (
            <Button
              primaryText="CANCEL"
              variant={actionButtonVariant}
              onClick={onSignUp}
              styleType={cancelButtonStyle}
              className="sign-up-button"
            />
          ) : isWaitlisted ? (
            isGameFull ? (
              <Button
                primaryText="CANCEL"
                variant="medium"
                onClick={onWaitlistToggle}
                styleType={cancelButtonStyle}
                className="waitlist-button"
              />
            ) : (
              <>
                <Button
                  primaryText="CANCEL"
                  variant={actionButtonVariant}
                  onClick={onWaitlistToggle}
                  styleType={cancelButtonStyle}
                  className="waitlist-button"
                />
                <Button
                  primaryText="JOIN"
                  variant={actionButtonVariant}
                  onClick={onSignUp}
                  styleType="default"
                  className="sign-up-button"
                />
              </>
            )
          ) : isGameFull ? (
            <Button
              primaryText="WAITLIST"
              variant="medium"
              onClick={onWaitlistToggle}
              styleType="default"
              className="waitlist-button"
            />
          ) : (
            <Button
              primaryText="JOIN"
              variant={actionButtonVariant}
              onClick={onSignUp}
              styleType="default"
              className="sign-up-button"
            />
          )}
        </div>
      </div>

      <div className="game-image-container">
        <img src={gameImage} alt="Game" className="game-image" />
        <div className="noiseeffect"></div>
      </div>
    </div>
  );
};

export default GameInfo;
